import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useLogin } from "@/api/hooks/useAuth";
import { extractErrorMessage } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import logoFarebne from "@/assets/tandem-logo-farebne.svg";
import { bezpecnyNavrat } from "@/lib/prihlasenie";

/**
 * Prihlasovacia stránka — centrovaná karta bez AppShell layoutu.
 *
 * Po úspešnom logine presmeruje na pôvodnú stránku: `?next=` (prežije aj
 * úplné načítanie), inak `location.state.from` od RequireAuth — **vrátane
 * query**, inak /zakazky. Do KOLA 127 sa bral len `pathname` a odkaz
 * `/zakazky?koncept=…` z Tandem Zostáv po prihlásení koncept neotvoril.
 */
export function LoginPage(): JSX.Element {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const login = useLogin();
  const navigate = useNavigate();
  const location = useLocation();

  const zoState = (
    location.state as {
      from?: { pathname?: string; search?: string; hash?: string };
    } | null
  )?.from;
  const from =
    bezpecnyNavrat(new URLSearchParams(location.search).get("next")) ??
    bezpecnyNavrat(
      zoState
        ? `${zoState.pathname ?? ""}${zoState.search ?? ""}${zoState.hash ?? ""}`
        : null,
    ) ??
    "/zakazky";

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!username.trim() || !password) return;
    login.mutate(
      { username: username.trim(), password },
      { onSuccess: () => navigate(from, { replace: true }) },
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background-alt px-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="items-center text-center">
          <img
            src={logoFarebne}
            alt="Tandem Rozpočet"
            className="mb-2 h-10 w-auto"
          />
          <CardTitle>Tandem Rozpočet</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="login-username">Prihlasovacie meno</Label>
              <Input
                id="login-username"
                autoFocus
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                maxLength={50}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="login-password">Heslo</Label>
              <Input
                id="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                maxLength={200}
                required
              />
            </div>
            {login.isError ? (
              <p role="alert" className="text-sm text-destructive">
                {extractErrorMessage(login.error)}
              </p>
            ) : null}
            <Button type="submit" className="w-full" disabled={login.isPending}>
              {login.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Prihlasujem…
                </>
              ) : (
                "Prihlásiť sa"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
